/* eslint-disable @typescript-eslint/unbound-method */

import { Component, DestroyRef, HostListener, inject, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { distinctUntilChanged, Subject, tap } from 'rxjs';
import {
    IndividualizationControlName,
    IndividualizationDialogModel,
    individualizationDialogResults,
    MaterialNumberDescription,
} from './individualization-material-serial.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSerNumber } from '@evorait/pace-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'pace-forms-individualization-material-serial',
    templateUrl: './individualization-material-serial.component.html',
    styleUrls: ['./individualization-material-serial.component.scss'],
})
export class IndividualizationMaterialSerialComponent implements OnInit {
    @Input()
    public materialNumbers: string[];

    @Input()
    public equipmentSets: MatSerNumber[];

    public model: IndividualizationDialogModel;
    public modalForm: FormGroup;
    public showMaterialDropdown = false;
    public showSerialDropdown = false;
    public modalResult = new Subject<individualizationDialogResults>();
    public filteredSerialNumber: MatSerNumber[];
    public materialNumberDescription: MaterialNumberDescription[] = [];
    private destroy = inject(DestroyRef);
    public constructor(
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
    ) {
        this.modalForm = this.formBuilder.group({
            [IndividualizationControlName.MATERIAL_NUMBER]: ['', Validators.required],
            [IndividualizationControlName.SERIAL_NUMBER]: ['', Validators.required],
            [IndividualizationControlName.DESCRIPTION]: [''],
        });

        this.model = new IndividualizationDialogModel(
            <string>this.translate.instant('INDIVIDUALIZATION_HEADER'),
            <string>this.translate.instant('INDIVIDUALIZATION_MATERIAL_NUMBER'),
            <string>this.translate.instant('INDIVIDUALIZATION_SERIAL_NUMBER'),
            <string>this.translate.instant('INDIVIDUALIZATION_DESCRIPTION'),
            <string>this.translate.instant('INDIVIDUALIZATION_SAVE'),
            <string>this.translate.instant('INDIVIDUALIZATION_CANCEL'),
        );
    }

    public ngOnInit(): void {
        this.filteredSerialNumber = [...this.equipmentSets];
        const serialNumberControl: FormControl = this.modalForm.get(
            IndividualizationControlName.SERIAL_NUMBER,
        ) as FormControl;

        this.modalForm
            .get(IndividualizationControlName.MATERIAL_NUMBER)
            ?.valueChanges.pipe(
                distinctUntilChanged(),
                tap((materialNumber) => {
                    this.showMaterialDropdown = false;
                    this.filteredSerialNumber = this.equipmentSets.filter(
                        (equipSet) => equipSet.matnr === materialNumber,
                    );
                    if (this.filteredSerialNumber.length === 1) {
                        serialNumberControl?.setValue(this.filteredSerialNumber[0].sernr);
                    } else {
                        serialNumberControl?.setValue('');
                    }
                }),
                takeUntilDestroyed(this.destroy),
            )
            .subscribe();
    }

    @HostListener('document:click', ['$event'])
    public onDocumentClick(event: MouseEvent): void {
        if (!(event.target as Element).closest('.individualization-dropdown')) {
            this.showMaterialDropdown = false;
            this.showSerialDropdown = false;
        }
    }

    public selectMaterialNumber(controlValue: MaterialNumberDescription): void {
        this.modalForm.get(IndividualizationControlName.MATERIAL_NUMBER)?.setValue(controlValue.materialDescription);
        this.showMaterialDropdown = false;
    }

    public selectSerialNumber(controlValue: string): void {
        this.modalForm.get(IndividualizationControlName.SERIAL_NUMBER)?.setValue(controlValue);
        this.showSerialDropdown = false;
    }

    public onSerialNumberInputFocus(): void {
        this.showSerialDropdown = true;
        this.showMaterialDropdown = false;
    }

    public onMaterialNumberInput(): void {
        const enteredMaterialValue: string = this.modalForm.get(IndividualizationControlName.MATERIAL_NUMBER)?.value;
        this.getMaterialNumberDescription(enteredMaterialValue);

        this.showMaterialDropdown = true;
        this.showSerialDropdown = false;
    }

    public onSubmit(): void {
        const results: individualizationDialogResults = this.modalForm.value;

        this.modalResult.next(results);
        this.modalResult.complete();
        this.modalInstance.hide();
    }

    public onCancel(): void {
        this.modalResult.complete();
        this.modalInstance.hide();
    }

    private getMaterialNumberDescription(filterMaterial: string): void {
        this.materialNumberDescription = [];
        this.materialNumbers.forEach((matNum) => {
            const equipSet = this.equipmentSets.find((equipment) => equipment.matnr === matNum);

            if (filterMaterial.length === 0 || matNum.indexOf(filterMaterial) > -1) {
                this.materialNumberDescription.push({
                    materialDescription: equipSet?.description ? `${matNum}-${equipSet.description}` : matNum,
                    materialNumber: matNum,
                });
            }
        });
    }
}
